import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight } from "lucide-react";
import { Reveal, SectionTitle } from "../components/common";
import { publicApi } from "../lib/api";
import VideoModal, { VideoThumb } from "../components/VideoModal";
import GoogleReviewsCarousel from "../components/GoogleReviewsCarousel";
import { CaseStudyCard } from "../components/CaseStudyCard";
import Seo from "../components/Seo";

export default function Testimonials() {
  const [videos, setVideos] = useState([]);
  const [stories, setStories] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => {
    publicApi.videoTestimonials().then(setVideos).catch(() => {});
    publicApi.caseStudies().then(setStories).catch(() => {});
  }, []);

  return (
    <div>
      <Seo title="Testimonials & Client Reviews" description="Hear directly from the growing businesses we support as their outsourced finance partner — video testimonials and genuine Google reviews for CASS accountants." />
      {active && <VideoModal videoId={active.youtubeId} title={active.title} onClose={() => setActive(null)} />}
      <section className="numbers-pattern text-white">
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-24">
          <span className="text-orange font-bold text-sm tracking-widest uppercase">Testimonials</span>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl uppercase leading-[0.95] mt-3">We're good at<br />what we do</h1>
          <p className="mt-5 text-slate-300 max-w-2xl leading-relaxed">Don't just take our word for it — hear from the businesses we're proud to support.</p>
        </div>
      </section>

      {videos.length > 0 && (
        <section className="py-20 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <Reveal><SectionTitle eyebrow="Watch" title="Video stories" /></Reveal>
            <div className="grid md:grid-cols-2 gap-6">
              {videos.map((v, i) => (
                <Reveal key={v.id} delay={i * 100}>
                  <button type="button" data-testid={`video-testimonial-${v.id}`} onClick={() => setActive(v)} className="w-full group block relative rounded-2xl overflow-hidden aspect-video shadow-lg">
                    <VideoThumb src={v.thumbnail} youtubeId={v.youtubeId} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-navy/50 group-hover:bg-navy/40 transition-colors flex items-center justify-center">
                      <span className="w-16 h-16 rounded-full bg-orange flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl"><Play size={26} className="text-white ml-1" fill="white" /></span>
                    </div>
                    <div className="absolute bottom-0 inset-x-0 p-5 bg-gradient-to-t from-navy-deep/90 to-transparent text-left"><p className="text-white font-semibold text-sm">{v.title}</p></div>
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {stories.length > 0 && (
        <section className="py-20 md:py-24 bg-white" data-testid="success-stories-section">
          <div className="max-w-7xl mx-auto px-6">
            <Reveal><SectionTitle eyebrow="Client Success Stories" title="Real results, real businesses" /></Reveal>
            <div className="grid md:grid-cols-3 gap-6">
              {stories.map((c, i) => (
                <Reveal key={c.id} delay={i * 80}>
                  <CaseStudyCard story={c} bg="bg-slate-50" showCta testid={`success-story-card-${i}`} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal><SectionTitle eyebrow="Google reviews" title="What our clients say" center /></Reveal>
          <GoogleReviewsCarousel />
        </div>
      </section>

      <section className="py-16 bg-navy text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl mb-4">Join our happy clients</h2>
          <p className="text-slate-300 mb-8">Your price is built from your own numbers, and it's published — work it out on our <Link to="/pricing" className="font-bold text-white underline decoration-orange/60 hover:decoration-orange">pricing page</Link> in about two minutes, no email needed. Then talk to us when you're ready.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5">Let's Talk <ArrowRight size={18} /></Link>
        </div>
      </section>
    </div>
  );
}
